import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import { useBoolean } from 'usehooks-ts';
import { isEmpty, map, range, sumBy } from 'lodash';
import moment from 'moment';

import { SalesLaborBlock } from '@pimm/services/lib/sms-workforce';
import { stringToDateLocal } from '@app/utils/date-formatter';
import { Daypart, useSiteTime } from '@app/features/store-core';
import { DaypartStatus, SalesBlock, SalesDaypart } from '../types';
import { useGetSalesAndLabor } from '../hooks/get-sales-and-labor.hook';
import { aggregateToHourSlot } from '../_helper';

type SalesLaborTrackerContextReturn = {
  displayForecastedSales?: boolean;
  hasLaborTracker?: boolean;
  partNumber?: number;
  isOperatingHours?: boolean;
  setIsOperatingHours: (_value: boolean) => void;
  sales: {
    dayparts: SalesDaypart[];
    total: Partial<SalesDaypart>;
  };
  dataThrough?: string | null;
  setPartNumber: (_partnumber?: number) => void;
};

type SalesLaborTrackerProviderProps = PropsWithChildren & {
  dayparts?: Daypart[];
  querySalesAndLabor: ReturnType<typeof useGetSalesAndLabor>;
};

const SalesLaborTrackerContext = createContext<SalesLaborTrackerContextReturn>(undefined!);

export const SalesLaborTrackerProvider = ({ children, dayparts: coreDayparts, querySalesAndLabor }: SalesLaborTrackerProviderProps) => {
  const siteTime = useSiteTime();
  const isOperatingHours = useBoolean(true);
  const [partNumber, setPartNumber] = useState<number | undefined>(undefined);

  useEffect(() => {
    setPartNumber(undefined);
  }, [coreDayparts?.[0]?.startTime?.getTime()]);

  const displayForecastedSales = querySalesAndLabor.data?.salesLaborData?.displayForecastedSales ?? false;
  const hasLaborTracker = querySalesAndLabor.data?.salesLaborData?.displayLaborHours ?? false;

  const dataThrough = useMemo(() => {
    const lastDataSyncUtc = querySalesAndLabor.data?.syncInfo?.lastDataSyncUtc;

    if (!lastDataSyncUtc || siteTime.utcOffsetInMinutes === undefined) return null;

    const syncLocal = moment.utc(lastDataSyncUtc).utcOffset(siteTime.utcOffsetInMinutes ?? 0);
    const flooredMinutes = Math.floor(syncLocal.minutes() / 30) * 30;
    syncLocal.minutes(flooredMinutes).seconds(0).milliseconds(0);

    const lagMs = siteTime.today().getTime() - syncLocal.valueOf();
    if (lagMs > 5 * 60_000) {
      return syncLocal.format('h:mm A');
    }
    return null;
  }, [querySalesAndLabor.data, siteTime]);

  // Build 48 × 30-min base entries from API blocks
  const salesBlocks: SalesBlock[] = useMemo(() => {
    if (!coreDayparts || coreDayparts.length === 0) return [];

    const blocks: SalesLaborBlock[] = querySalesAndLabor.data?.salesLaborData?.blocks ?? [];
    const dayStart = coreDayparts[0].startTime;

    // Convert lastDataSyncUtc (real UTC) into the local-as-Z epoch space used by block.dayDateTime.
    // block.dayDateTime stores local time with a fake Z suffix, so new Date(dayDateTime).getTime()
    // treats it as UTC. Adding utcOffsetInMinutes brings lastDataSyncUtc into the same epoch space.
    const lastDataSyncUtc = querySalesAndLabor.data?.syncInfo?.lastDataSyncUtc;
    const syncCutoffMs =
      lastDataSyncUtc && siteTime.utcOffsetInMinutes !== undefined
        ? new Date(lastDataSyncUtc).getTime() + siteTime.utcOffsetInMinutes * 60_000
        : undefined;

    const entries = range(48).map(i => {
      const time = moment(dayStart).add(30 * i, 'minutes');
      const block = blocks.find(_ => _.dayDateTime && stringToDateLocal(_.dayDateTime)!.getTime() === time.valueOf());

      // A block is "actual" only when the full 30-min slot end is at or before the sync cutoff.
      // Falls back to !!block when syncCutoffMs is unavailable.
      const blockEndMs = block?.dayDateTime ? new Date(block.dayDateTime).getTime() + 30 * 60 * 1_000 : undefined;
      const hasActual = blockEndMs !== undefined && syncCutoffMs !== undefined ? blockEndMs <= syncCutoffMs : !!block;

      return {
        startTime: time.toDate(),
        endTime: time.add(30, 'minutes').toDate(),
        hasActual,
        salesActual: block?.salesActual ?? 0,
        salesForecast: block?.salesForecasted ?? 0,
        salesVar: block?.salesVariance ?? 0,
        salesCumVar: block?.salesCumulativeVariance ?? 0,
        salesCumActual: 0, // computed as running sum per view in useMemo below
        laborScheduled: block?.laborScheduledTotal ?? 0,
        laborSchedSvc: block?.laborScheduledService ?? 0,
        laborSchedNS: block?.laborScheduledNonService ?? 0,
        laborGuide: block?.laborGuideTotal ?? 0,
        laborGuideSvc: block?.laborGuideService ?? 0,
        laborGuideNS: block?.laborGuideNonService ?? 0,
        laborActual: block?.laborActualTotal ?? 0,
        laborSalary: block?.laborActualSalary ?? 0,
        laborHourly: block?.laborActualHourly ?? 0,
        laborVar: block?.laborGuideVariance ?? 0,
        laborCumVar: block?.laborCumulativeGuideVariance ?? 0,
      };
    });

    return entries;
  }, [coreDayparts, querySalesAndLabor.data, siteTime]);

  const sales = useMemo(() => {
    if (isEmpty(coreDayparts)) return { dayparts: [], total: {} };

    const now = siteTime.today();

    // Enumerate each daypart with sales data
    const dayparts = map(coreDayparts, _dp => {
      const rawBlocks = salesBlocks.filter(_ => _.startTime >= _dp.startTime && _.startTime < _dp.endTime);
      const completedSales = rawBlocks.filter(_ => _.hasActual);
      let status: DaypartStatus = 'active';

      if (_dp.startTime > now) status = 'future';
      if (_dp.endTime <= now) {
        status = rawBlocks.some(b => !b.hasActual) ? 'pending' : 'completed';
      }

      // Compute running cumulative sums scoped to this daypart
      let cumSalesVar = 0;
      let cumSalesActual = 0;
      let cumLaborVar = 0;
      const blocks = rawBlocks.map(block => {
        if (block.hasActual) {
          cumSalesVar += block.salesVar;
          cumSalesActual += block.salesActual;
          cumLaborVar += block.laborVar;
        }
        return { ...block, salesCumVar: cumSalesVar, salesCumActual: cumSalesActual, laborCumVar: cumLaborVar };
      });

      return {
        ..._dp,
        timeStart: moment(_dp.startTime).format('h:mm A'),
        timeEnd: moment(_dp.endTime).format('h:mm A'),
        status,
        actual: Math.round(sumBy(completedSales, 'salesActual')),
        elapsedForecast: Math.round(sumBy(completedSales, 'salesForecast')),
        forecast: Math.round(sumBy(rawBlocks, 'salesForecast')),
        blocks,
      } satisfies SalesDaypart;
    });

    const day = siteTime.toStartEndOfPart(dayparts[0].startTime);
    const opHours = siteTime.toOperationHours(day.startTime);
    const staffStart = opHours?.staffStart;
    const storeClose = opHours?.storeClose;
    const hourlyBlocks: SalesBlock[] = [];
    let cumSalesActual = 0;

    for (const hr of range(24)) {
      const startTime = moment(day.startTime).add(hr, 'hour').toDate();
      const endTime = moment(startTime).add(1, 'hour').toDate();

      if (isOperatingHours.value && staffStart && startTime <= staffStart) continue;
      if (isOperatingHours.value && storeClose && startTime >= storeClose) continue;

      const blocks = salesBlocks.filter(_ => _.startTime >= startTime && _.startTime < endTime);
      const slot = { startTime, endTime, ...aggregateToHourSlot(blocks) } as SalesBlock;

      if (slot.hasActual) {
        cumSalesActual += slot.salesActual;
      }
      hourlyBlocks.push({ ...slot, salesCumActual: cumSalesActual });
    }

    const completedSalesBlocks = salesBlocks.filter(b => b.hasActual);
    const total: Partial<SalesDaypart> = {
      startTime: day.startTime,
      endTime: day.endTime,
      actual: Math.round(sumBy(completedSalesBlocks, 'salesActual')),
      elapsedForecast: Math.round(sumBy(completedSalesBlocks, 'salesForecast')),
      forecast: Math.round(sumBy(salesBlocks, 'salesForecast')),
      status: day.endTime < now ? 'completed' : 'active',
      blocks: hourlyBlocks,
    };

    return {
      dayparts,
      displayForecastedSales,
      total,
    };
  }, [coreDayparts, isOperatingHours.value, salesBlocks]);

  return (
    <SalesLaborTrackerContext.Provider
      value={{
        displayForecastedSales,
        hasLaborTracker,
        sales,
        dataThrough,
        isOperatingHours: isOperatingHours.value,
        setIsOperatingHours: isOperatingHours.setValue,
        partNumber,
        setPartNumber,
      }}
    >
      {children}
    </SalesLaborTrackerContext.Provider>
  );
};

export const useSalesLaborTracker = <T,>(selector: (context: SalesLaborTrackerContextReturn) => T): T =>
  useContextSelector(SalesLaborTrackerContext, selector);
